import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { finalize, shareReplay } from 'rxjs/operators';
import { BankAccount } from '../../modules/bookkeeping/models/account/bank-account'; 
import { CurrentUserModel } from '../model/register-model';

import { HttpInternalService } from './http.service';
import { environment } from 'src/environments/environment';
import { AppConfigService } from 'src/app/appConfig.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private get baseUrl(): string {
    return this.configService.apiURL;
  }

  loggedUserDetails: BehaviorSubject<CurrentUserModel> = new BehaviorSubject<CurrentUserModel>(null);
  private pendingCurrentUser: Observable<any>;

  constructor(private _httpService: HttpInternalService, private http: HttpClient, private configService: AppConfigService) { }

  private userRole = new BehaviorSubject<number>(0);
  userRole$ = this.userRole.asObservable();

  setUserRoleData(data: number) {
      this.userRole.next(data);
  }

  // SD-1137 PR-FE4: source of truth for the current org's capture methods bitmask.
  // 0 = unknown / legacy orgs — consumers should fall back to a generic empty state.
  // Matches Synced.Models.CaptureMethods [Flags]: Email=1, Paper=2, Pdf=4, Later=8.
  private captureMethods = new BehaviorSubject<number>(0);
  captureMethods$ = this.captureMethods.asObservable();

  loadCaptureMethods(organizationId: string): void {
    if (!organizationId) {
      this.captureMethods.next(0);
      return;
    }
    this._httpService.getRequest<any>('Account/GetCaptureMethods', { organizationId })
      .subscribe(
        (res) => {
          const value = res?.data?.captureMethods ?? 0;
          this.captureMethods.next(value);
        },
        () => { this.captureMethods.next(0); }
      );
  }

  // Convenience helpers for empty-state consumers.
  hasCaptureMethod(bit: number): boolean {
    return (this.captureMethods.value & bit) === bit;
  }

  get currentCaptureMethods(): number {
    return this.captureMethods.value;
  }

  set upUserDetails(data: string) {
    localStorage.setItem('UpUserDetails', data);
  }

  get upUserDetails(): string {
    return localStorage.getItem('UpUserDetails');
  }

  getSelectedUser(url: string, userId: string) {
    return this._httpService.getRequest(`${url + "?UserId=" + userId}`);
  }

  getUserGetById(userId: string) {
    return this._httpService.getRequest<any>(`${"Account/UserGetById" + "?UserId=" + userId}`);
  }

  getUsers(url: string, organizationId: string) {
    return this._httpService.getRequest(`${url + "?Id=" + organizationId}`);
  }

  userActivationOrDeletion(url: string, userId: string, status: boolean, activeDelete: string, organizationId: string) {
    return this._httpService.deleteRequest(`${url + "?Id=" + userId + "&IsActive=" + status + "&ActivationParameter=" + activeDelete + "&organizationId=" + organizationId}`);
  }

  userInsertionOrUpdation(url: string, registerModel) {
    return this._httpService.postRequest(url, JSON.stringify(registerModel));
  }
  getAllUsersByOrganizationId(organizationId: string, userId) {
    return this._httpService.getRequest<any>(`${"Reporting/GetUsersByOrganizationId" + "?organisationId=" + organizationId + "&userId=" + userId}`);
  }
  getUsersByOrganizationId(organizationId: string) {
    return this._httpService.getRequest<any>(`${"Reporting/GetAllUsersByOrganizationId" + "?organisationId=" + organizationId}`);
  }
  getDeletedUsers(url: string, organisationId: string) {
    return this._httpService.getRequest(`${url + "?organisationId=" + organisationId}`);
  }
  ReassignedTaskToUser(registerModel) {
    return this._httpService.postRequest<any>(`${"Reporting/ReassignedTaskToUser"}`, JSON.stringify(registerModel));
  }
  assignOrganizationsToAllUsers(url: string, registerModel) {
    return this._httpService.postRequest(url, JSON.stringify(registerModel));
  }

  updateUserLastLogin(model: any) {
    return this._httpService.postRequest(`${"Account/UpdateUserLastLogin"}`, JSON.stringify(model));
  }

  insertUpdateUserBankDetail(model: BankAccount) {
    return this._httpService.postRequest<any>(`${"Account/InsertUpdateUserBankDetail"}`, JSON.stringify(model));
  }

  UpdateUserAccountGroup(groupId, userId, orgId) {
    return this._httpService.getRequest<any>(`Account/UpdateUserAccountGroup?groupId=${groupId}&userId=${userId}&organizationId=${orgId}`);
  }

  UpdateAccountEmoji(emoji, accountId, orgId) {
    // Encoded: some emoji include characters such as # that would end the query string.
    return this._httpService.getRequest<any>(`Account/UpdateAccountEmoji?emoji=${encodeURIComponent(emoji)}&accountId=${accountId}&organizationId=${orgId}`);
  }

  // --- Record Assignment APIs ---
  getAssignedUsers(recordId: string, recordTypeId: number, isCurrent: boolean) {
    return this._httpService.getRequest<any>(
      "recordassignment?recordId=" + recordId + "&recordTypeId=" + recordTypeId + "&isCurrent=" + isCurrent
    );
  }

  createAssignment(body: { userId: string, assignedBy: string, recordId: string, recordTypeId: number, isCurrent: boolean }) {
    // Ensure assignedBy is the full GUID, not a short id
    let assignedBy = localStorage.getItem('UpUserId') || localStorage.getItem('UserId');
    // If assignedBy is a short id, try to get the full id from upUserDetails
    if (assignedBy && assignedBy.length <= 4) {
      const upUserDetails = localStorage.getItem('UpUserDetails');
      if (upUserDetails) {
        try {
          const details = JSON.parse(upUserDetails);
          if (details && details.userGuidId) {
            assignedBy = details.userGuidId;
          }
        } catch {}
      }
    }
    return this._httpService.postRequest<any>(
      "recordassignment",
      { ...body, assignedBy }
    );
  }

  updateAssignment(body: { userId: string, recordId: string, recordTypeId: number, isCurrent: boolean }) {
    return this._httpService.putRequest<any>(
      "recordassignment",
      body
    );
  }

  setUserApprover(payload: { userid: string; approverid: string; organisationid: string }) {
    return this.http.post<{ status: string; message: string }>(
      `${this.baseUrl}Organisation/setuserapprover`,
      payload
    );
  }

  getUserApprover(organisationId: string, userId: string) {
    return this.http.get<{ approverid: string }>(
      `${this.baseUrl}Organisation/getuserapprover?organisationId=${organisationId}&userId=${userId}`
    );
  }

    getCanPublish(organisationId: string, userId: string) {
    return this.http.get<{ canPublish: boolean }>(
      `${this.baseUrl}Organisation/getuserCanPublish?organisationId=${organisationId}&userId=${userId}`
    );
  }

  getAllUsers() {
    return this.http.get<{ data: any[] }>(`${this.baseUrl}/Account/UsersGetAll`);
  }

  resendInvite(url: string, registerModel) {
    return this._httpService.postRequest(url, JSON.stringify(registerModel));
  }

  enterGodMode(userModel: any) {
    return this._httpService.getRequest("Organisation/EnterGodMode?userId=" + userModel.userId + "&organizationId=" + userModel.organizationId);
  }
  exitGodMode(userModel: any) {
    return this._httpService.getRequest("Organisation/ExitGodMode?userId=" + userModel.userId + "&organizationId=" + userModel.organizationId);
  }
  updateUserAISettings(payload: {  organizationId: string, userId: string; aiEnabled: boolean }) {
    return this._httpService.getRequest("Account/UpdateUserAISettings?organizationId=" + payload.organizationId + "&userId=" + payload.userId + "&aiEnabled=" + payload.aiEnabled);
  }
  /**
   * The signed-in user. Around 27 components ask for it, many while a page loads, so
   * calls made while one is in flight share it; once it completes the next call is fresh.
   */
  getUserGetByIdServer(): Observable<any> {
    if (!this.pendingCurrentUser) {
      this.pendingCurrentUser = this._httpService.getRequest<any>(`${"Account/UserGetByIdServer"}`).pipe(
        finalize(() => this.pendingCurrentUser = null),
        shareReplay(1));
    }
    return this.pendingCurrentUser;
  }
}
